import { useEffect, useState } from 'react';
import { isRestComplete, restOverdueSeconds, restProgress, restRemainingSeconds, type RestTimer } from '@liftx/core';
import type { ActiveRestTimer } from '../../data/types';

export interface RestTimerDisplay {
  remainingSeconds: number;
  overdueSeconds: number;
  progress: number;
  isComplete: boolean;
}

const IDLE: RestTimerDisplay = { remainingSeconds: 0, overdueSeconds: 0, progress: 1, isComplete: true };

/**
 * Live display for a persisted rest timer. Master Phase 6.
 *
 * A thin, stateless-by-design wrapper: `rest-timer.ts`'s functions derive
 * everything from `{startedAt, endsAt}` against the current instant, so this
 * hook's only job is supplying that instant on a loop and doing zero work
 * when it wouldn't be seen — `requestAnimationFrame` runs **only while the
 * document is visible** (Phase 0 §18's battery rule), and simply stops
 * scheduling itself when hidden rather than continuing to tick unseen.
 */
export function useRestTimer(timer: ActiveRestTimer | null): RestTimerDisplay {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (timer === null) return;

    let frame: number | null = null;

    function scheduleIfVisible(): void {
      if (document.visibilityState !== 'visible') return;
      frame = requestAnimationFrame(() => {
        setNow(Date.now());
        scheduleIfVisible();
      });
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        if (frame === null) scheduleIfVisible();
      } else if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    }

    setNow(Date.now());
    scheduleIfVisible();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [timer]);

  if (timer === null) return IDLE;

  const asRestTimer: RestTimer = {
    startedAt: timer.startedAt,
    endsAt: timer.endsAt,
    durationS: (timer.endsAt - timer.startedAt) / 1000,
  };

  return {
    remainingSeconds: restRemainingSeconds(asRestTimer, now),
    overdueSeconds: restOverdueSeconds(asRestTimer, now),
    progress: restProgress(asRestTimer, now),
    isComplete: isRestComplete(asRestTimer, now),
  };
}
